import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export interface IUploadModalSelectorProps {
  children: ReactNode;
}

export interface IUploadModalSelectorData {
  isShowModal: boolean;
  isLoadingUpload: boolean;
  setIsLoadingUpload(status: boolean): void;
  showUpload(): void;
  closeUpload(): void;
}

export const UploadModalSelectorContext = createContext<IUploadModalSelectorData>(null);

export const UploadModalSelector = ({ children }: IUploadModalSelectorProps) => {
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const [isLoadingUpload, setIsLoadingUpload] = useState<boolean>(false);

  const showUpload = useCallback(() => {
    setIsShowModal(true);
  }, []);

  const closeUpload = useCallback(() => {
    setIsShowModal(false);
  }, []);

  // Listen for custom event to open the upload modal
  useEffect(() => {
    const handleOpenUploadModal = () => {
      showUpload();
    };

    window.addEventListener('open-upload-modal', handleOpenUploadModal);

    return () => {
      window.removeEventListener('open-upload-modal', handleOpenUploadModal);
    };
  }, [showUpload]);

  const value = useMemo(
    () => ({
      isShowModal,
      isLoadingUpload,
      setIsLoadingUpload,
      showUpload,
      closeUpload,
    }),
    [isShowModal, isLoadingUpload, setIsLoadingUpload, showUpload, closeUpload]
  );

  return <UploadModalSelectorContext.Provider value={value}>{children}</UploadModalSelectorContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function useUploadModalSelector() {
  const context = useContext(UploadModalSelectorContext);

  if (!context) {
    throw new Error(`No UploadModal selector compound components cannot be rendered outside the UploadModal Selector component`);
  }
  return context;
}
